import { ApiConstant, KeyConstant, SystemConstant } from "const";
import { LocalAppNotificationService, LocalDbManagement, getInteractor } from "services/local.service";
import {
  StorageUtil,
  convertString2JSON,
  getPrefixKey,
  removeDuplicateInArray,
  toCamel,
  toSnake,
  isNewIvsE2EDevice,
  getDataFromPrefixKey,
  isJSONString,
} from "utils";
import { checkCurrentBranchByPrefix, checkTriggerMessageUI, formatPagingParams } from "../../../sagas/saga.helper";
import { remoteApiFactory } from "services";
import { saveKeysOfDevice } from "../../../sagas/account-key.saga";
import store, { ConversationActions, SystemActions } from "redux-store";
import { updateThread } from "../../../sagas/thread.saga";
import { updateMessageStatus } from "../../../sagas/conversation-message.saga";
import { sortBy, uniqWith, isEqual, uniqBy } from "lodash";
import { getDevice } from "../device.service";
import { handleNewConversation } from "../conversation.service";
import { handleCallingMessage } from "./call-message";
import { sendMessageEncryptStatus } from "./encrypt-status-message";
import { checkCurrentGroup, checkCurrentThread, checkFocusedApp, checkingPushNotification } from "utils/view.utils";
import { pushNotificationList } from "./push-notification";
import { checkValidGroupSettingOff } from "pages/HomePage/components/Account/SettingApp/SettingAppNotification/setting-time.helper";
import CipherMessageManager from "../cipher/CipherMessageManager";
import { updateParentDeleteMessageContent } from "services/message.service";
import { sendOneMessageSenderKeyDistributionMessage } from "./error-message";
import { getFileList } from "../file.service";
import { restoreThread } from "../../../sagas/restore.saga";

const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;
const cipherMessageManager = new CipherMessageManager();

export const getMessage = async (prefixKey, time2FetchMessage) => {
  try {
    const localTime2FetchMessage = StorageUtil.getItem(KeyConstant.KEY_TIME_2_FETCH_MESSAGE, prefixKey) || 0;
    let lastMessage = await getInteractor(prefixKey).LocalMessageService.getLastMessage();
    const minTime2Fetching = Math.min(lastMessage?.created, time2FetchMessage, localTime2FetchMessage) || 0;

    // Call API to get ${LIMIT_MESSAGE_API} message until get all new message on server
    let fetchLimit = 100;
    const messageParams = formatPagingParams({
      isOnline: true,
      limit: fetchLimit,
      offset: 0,
      paging: 1,
      since_time: minTime2Fetching,
    });

    const response = await remoteApiFactory.getBranchApi(prefixKey).getMessageList(toSnake(messageParams));
    const responseData = Array.isArray(response.data?.data) ? response.data.data : [];
    if (response.status === ApiConstant.STT_OK && responseData.length > 0) {
      const remoteMessageList = sortBy(responseData, [message => message.created]); // ASC sort by created
      await handleRemoteMessageList(prefixKey, remoteMessageList, fetchLimit);
      await setUnreadInAppLogo();

      lastMessage = await getInteractor(prefixKey).LocalMessageService.getLastMessage();
      const maxTime2Fetching = Math.max(lastMessage?.created, localTime2FetchMessage) || 0;
      StorageUtil.setItem(KeyConstant.KEY_TIME_2_FETCH_MESSAGE, maxTime2Fetching, prefixKey);
    }
  } catch (error) {
    console.error("fetch message error: ", error);
    return false;
  }

  return true;
};

const handleRemoteMessageList = async (prefixKey, remoteMessageList, fetchLimit) => {
  if (!Array.isArray(remoteMessageList)) throw new Error("remoteMessageList is not array");

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const noticeMessageList = [];

  // 1. Synch keys of all devices to prepare decrypt remote message
  let deviceIds = remoteMessageList.map(item => ({
    deviceId: item.sender_device_id,
    accountId: item.sender_id,
  }));
  deviceIds = uniqWith(deviceIds, isEqual); // Remove duplicate data
  for (let deviceIndex = 0; deviceIndex < deviceIds.length; deviceIndex++) {
    const deviceFrom = deviceIds[deviceIndex];
    let deviceLocal = await getInteractor(prefixKey).LocalDeviceService.get(deviceFrom.deviceId);
    const deviceLocalByMac = await getInteractor(prefixKey).LocalDeviceService.getDeviceByMac(deviceFrom.deviceId);
    if (deviceLocalByMac && !deviceLocal) deviceLocal = deviceLocalByMac;
    if (!deviceLocal) {
      deviceLocal = await getDevice(prefixKey, deviceFrom.accountId, deviceFrom.deviceId);
    }

    if (deviceLocal && deviceLocal.key_f !== SystemConstant.DEVICE_KEY_STATE.correct) {
      await saveKeysOfDevice(prefixKey, {
        accountId: deviceLocal.account_id,
        deviceId: deviceLocal.id,
      });
    }
  }

  // 2. Handling message
  let isNeedUpdateUI = true;
  const uniqueGroupId = uniqBy(remoteMessageList, message => message.group_id).map(item => item.group_id);
  let groups = {};
  const handlingMessagePromises = uniqueGroupId.map(async groupId => {
    const group = await getGroup(groupId, prefixKey);
    if (group && group.id) {
      const accountGroup = await getInteractor(prefixKey).LocalAccountGroupService.findOne({
        account_id: accountId,
        group_id: group.id,
        state: SystemConstant.STATE.active,
      });
      group.accountGroupRecallTime = accountGroup?.recall_time;
      groups[group.id] = group;
    }
  });

  await Promise.all(handlingMessagePromises);
  remoteMessageList = sortBy(remoteMessageList, [message => message.created]);

  for (let i = 0; i < remoteMessageList.length; i++) {
    let remoteMessage = toCamel(remoteMessageList[i]);
    const group = groups[remoteMessage.groupId];
    if (group && group.id) {
      const localMessage = await getInteractor(prefixKey).LocalMessageService.get(remoteMessage.id);
      if (remoteMessage.sendType !== SystemConstant.SEND_TYPE.restoreData && !(localMessage && localMessage.id)) {
        const handlingMessageResult = await handleMessage(
          remoteMessage,
          group,
          prefixKey,
          group.accountGroupRecallTime,
        );
        isNeedUpdateUI = Boolean(handlingMessageResult.isNeedUpdateUI);
        // Check created time message and confirm message need to show or not
        if (await checkingPushNotification(handlingMessageResult.noticeMessage?.message, prefixKey)) {
          noticeMessageList.push(handlingMessageResult.noticeMessage);
        }
      } else if (localMessage && localMessage.id) {
        if (localMessage?.message_encrypt_status !== SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover) {
          await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(remoteMessage)]);
        }
      }
    }
  }
  const selectedGroupId = store.getState().conversationRedux.selectedGroupId;

  let messageInGroup = [];
  //2.1. Handling update message after done (support case switch group in push)
  for (let i = 0; i < remoteMessageList.length; i++) {
    let remoteMessage = toCamel(remoteMessageList[i]);
    const group = groups[remoteMessage.groupId];
    if (group && group.id && selectedGroupId && selectedGroupId === group.id) {
      messageInGroup.push(remoteMessageList);
    }
  }
  if (messageInGroup.length > 0) {
    store.dispatch(
      ConversationActions.conversationSet({
        updateThreadIds: removeDuplicateInArray(messageInGroup.map(item => item.thread_id)),
      }),
    );
  }

  // 3. Show notification
  const noticeMsgList = [];
  for (let index = 0; index < noticeMessageList.length; index++) {
    const { prefixKey, message } = noticeMessageList[index];

    // Skip show notification if message is read/ seen
    const seenMessageRecord = await getInteractor(prefixKey).LocalSeenMemberService.findOne({
      source_id: message.sourceId,
      member_account_id: accountId,
    });

    if (false === Boolean(seenMessageRecord && seenMessageRecord.id)) {
      if (message.state !== SystemConstant.STATE.inactive) noticeMsgList.push(message);
    }
  }
  await pushNotificationList(prefixKey, noticeMsgList);

  // Trigger disappear timeout => cover case my message is sent from multiple device
  const minDisappearTime = await getInteractor(prefixKey).LocalMessageService.getMinDisappearTime();
  if (minDisappearTime > 0) {
    store.dispatch(
      ConversationActions.conversationSet({
        disappearTime: minDisappearTime,
      }),
    );
  }

  // 4. Update message status
  // Send "READ" status to server if message is in selectedGroup
  // Message not in selected group: Send "RECEIVED" status to server
  const normalMessages = remoteMessageList.filter(
    mes =>
      mes.send_type !== SystemConstant.SEND_TYPE.senderKey &&
      mes.send_type !== SystemConstant.SEND_TYPE.keyError &&
      mes.send_type !== SystemConstant.SEND_TYPE.encryptStatus &&
      false === SystemConstant.ARR_CALLING_TYPES.includes(mes.send_type) &&
      mes.sender_id !== accountId,
  );

  const callMessages = remoteMessageList.filter(
    mes => SystemConstant.ARR_CALLING_TYPES.includes(mes.send_type) && mes.sender_id !== accountId,
  );

  const readNormalMsgIds = [];
  normalMessages.forEach(mes => {
    const isNotUpdateLastMessage = SystemConstant.NOT_UPDATE_LAST_MESSAGE_SEND_TYPE.includes(mes.send_type);
    const isUpdateReadGroup =
      mes.status !== SystemConstant.MESSAGE_STATUS.read &&
      false === Boolean(mes.thread_id) &&
      checkCurrentGroup(mes.group_id) &&
      checkFocusedApp();

    const isUpdateReadThread =
      mes.status !== SystemConstant.MESSAGE_STATUS.read &&
      Boolean(mes.thread_id) &&
      checkCurrentThread(mes.thread_id) &&
      checkFocusedApp();

    if (isNotUpdateLastMessage || isUpdateReadGroup || isUpdateReadThread) {
      readNormalMsgIds.push(mes.id);
    }
  });

  const readCallMsgIds = [];
  callMessages.forEach(mes => {
    if (
      mes.status !== SystemConstant.MESSAGE_STATUS.read &&
      mes.call_status !== SystemConstant.MESSAGE_CALL_STATUS.missed
    ) {
      readCallMsgIds.push(mes.id);
    }
  });

  const readMessageIds = readNormalMsgIds.concat(readCallMsgIds);
  if (readMessageIds.length > 0) {
    await updateMessageStatus({
      data: {
        messageIds: readMessageIds,
        status: SystemConstant.MESSAGE_STATUS.read,
      },
      prefixKey,
    });

    // Update notification status if exist mention messages
    const mentionMsgIds = normalMessages
      .filter(message => {
        const mentionList = message.mentions ? convertString2JSON(message.mentions, []) : null;
        return Array.isArray(mentionList) && mentionList.length > 0 && readMessageIds.includes(message.id);
      })
      .map(item => item.id);
    const noticeRecords = await getInteractor(prefixKey).LocalNotificationService.find({
      message_ids: mentionMsgIds,
      status: Object.values(SystemConstant.NOTIFICATION_STATUS).filter(
        stt => stt !== SystemConstant.NOTIFICATION_STATUS.read,
      ),
    });
    await updateMessageStatus({
      data: {
        notifyIds: noticeRecords.map(item => item.id),
        status: SystemConstant.MESSAGE_STATUS.read,
      },
    });
  }

  const [_, branchId] = getDataFromPrefixKey(prefixKey);
  const notReceivedLocalMsgIds = await getInteractor(prefixKey).LocalMessageService.findNotReceived(
    branchId,
    fetchLimit + 50,
    0,
  );
  const receivedMessageIds = notReceivedLocalMsgIds?.map(item => item.id) || [];
  if (receivedMessageIds.length > 0) {
    await updateMessageStatus({
      data: {
        messageIds: receivedMessageIds,
        status: SystemConstant.MESSAGE_STATUS.received,
        modified: Date.now(),
      },
      prefixKey,
    });
  }

  const isCurrentBranch = checkCurrentBranchByPrefix(prefixKey);
  if (isCurrentBranch && isNeedUpdateUI) {
    store.dispatch(
      ConversationActions.conversationSet({
        isUpdateViewMode: Date.now(),
      }),
    );
  }
};

const getGroup = async (groupId, prefixKey) => {
  let group = await getInteractor(prefixKey).LocalGroupService.get(groupId);
  // Group is not exist in local db => sync group
  if (!(group && group.id)) {
    const isSynchGroup = await handleNewConversation(prefixKey, groupId);
    if (isSynchGroup) {
      group = await getInteractor(prefixKey).LocalGroupService.get(groupId);
    }
  }

  // Check if selected group is draft => update selectedGroupId
  const groupMemberIds = group.groupMembers.map(item => item.id);

  const selectedGroupId = store.getState().conversationRedux.selectedGroupId;
  const selectGroupDetail = await getInteractor(prefixKey).LocalGroupService.get(selectedGroupId);
  const draftMemberIds = selectGroupDetail.draftGroupMembers;

  if (
    selectGroupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal &&
    draftMemberIds &&
    isEqual(groupMemberIds.sort(), draftMemberIds.sort())
  ) {
    store.dispatch(
      ConversationActions.setSelectGroupId({
        selectedGroupId: groupId,
      }),
    );
    getInteractor(prefixKey).LocalGroupService.delete(selectGroupDetail.id);
  }

  return group;
};

const handleMessage = async (newMessage, group, prefixKey) => {
  if (false === Boolean(newMessage && newMessage.id)) return;
  let isNeedUpdateUI = false;
  let noticeMessage;
  const messageOptions = convertString2JSON(newMessage.options, {});

  const encryptVersion = messageOptions.encrypt_version;
  const indexSendNumber = messageOptions.index_send_number;
  const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
  const device = await getInteractor(prefixKey).LocalDeviceService.get(newMessage.senderDeviceId);

  try {
    let encryptStatusInfo = null;
    switch (newMessage.sendType) {
      case SystemConstant.SEND_TYPE.senderKey:
        if (group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal) {
          await resetIndexFlowOldVersion(newMessage, group, device, messageOptions, encryptTypeProtocol, prefixKey);
          encryptStatusInfo = await cipherMessageManager.senderKeyMessageUtils.senderMessageProcess(
            newMessage,
            messageOptions,
            prefixKey,
            encryptTypeProtocol,
          );
          //Nếu msg đã được vô hiệu thì không cần xử lý nữa
          if (newMessage.state === SystemConstant.STATE.inactive) break;
          if (encryptStatusInfo != null) {
            await sendMessageEncryptStatus(prefixKey, group, newMessage, encryptTypeProtocol, encryptStatusInfo);
          }
          await resetIndexFlowOldVersion(newMessage, group, device, messageOptions, encryptTypeProtocol, prefixKey);
        }
        await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)]);
        break;

      case SystemConstant.SEND_TYPE.keyError:
        await cipherMessageManager.decryptNormalMessage.decryptFailedMessages(
          newMessage,
          group,
          encryptTypeProtocol,
          prefixKey,
        );
        encryptStatusInfo = await cipherMessageManager.encryptionErrorNotifier.handleErrorMessage(
          newMessage,
          group,
          device,
          messageOptions,
          encryptTypeProtocol,
          prefixKey,
        );

        await sendMessageEncryptStatus(prefixKey, group, newMessage, encryptTypeProtocol, encryptStatusInfo);
        isNeedUpdateUI =
          (await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)])) || isNeedUpdateUI;
        break;

      case SystemConstant.SEND_TYPE.senderKeyDeliveryError:
        await cipherMessageManager.encryptionErrorNotifier.handleErrorMessage(
          newMessage,
          group,
          device,
          messageOptions,
          encryptTypeProtocol,
          prefixKey,
        );
        await sendOneMessageSenderKeyDistributionMessage(prefixKey, group, newMessage, encryptTypeProtocol);
        await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)]);
        break;

      case SystemConstant.SEND_TYPE.botMessage:
        await cipherMessageManager.botMessage.botMessageProcess(newMessage, messageOptions, prefixKey);
        const messageProcessingResult = await processMessageContent(newMessage, group, prefixKey, encryptTypeProtocol);
        isNeedUpdateUI = messageProcessingResult.isNeedUpdateUI;
        noticeMessage = messageProcessingResult.noticeMessage;
        break;

      case SystemConstant.SEND_TYPE.encryptStatus:
        const messageError = await cipherMessageManager.encryptStatus.resolveEncryptStatus(
          newMessage,
          group,
          prefixKey,
        );
        await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)]);
        const messageEncryptStatusContentJson = newMessage.content ? convertString2JSON(newMessage.content, {}) : {};
        switch (messageError.type) {
          case SystemConstant.MESSAGE_ERROR_TYPE.RESET_SENDER_KEY_ERROR:
            await cipherMessageManager.encryptionErrorNotifier.handleErrorMessage(
              newMessage,
              group,
              device,
              messageOptions,
              encryptTypeProtocol,
              prefixKey,
            );
            await sendOneMessageSenderKeyDistributionMessage(
              prefixKey,
              group,
              newMessage,
              messageEncryptStatusContentJson.encrypt_version,
              messageError.newIndexSend,
            );
            break;
          case SystemConstant.MESSAGE_ERROR_TYPE.RESET_MESSAGE_ERROR:
            break;
          default:
            break;
        }
        break;

      default:
        await resetIndexFlowOldVersion(newMessage, group, device, messageOptions, encryptTypeProtocol, prefixKey);
        const validateList = await cipherMessageManager.decryptNormalMessage.validateRecoverMessages(
          newMessage,
          group,
          prefixKey,
          encryptTypeProtocol,
        );
        if (validateList.lastMessage) {
          isNeedUpdateUI = true;
        }
        encryptStatusInfo = await cipherMessageManager.decryptNormalMessage.decryptNormalMessage(
          newMessage,
          group,
          messageOptions,
          prefixKey,
          indexSendNumber,
        );
        await resetIndexFlowOldVersion(newMessage, group, device, messageOptions, encryptTypeProtocol, prefixKey);
        const result = await processMessageContent(
          newMessage,
          group,
          prefixKey,
          encryptTypeProtocol,
          encryptStatusInfo,
        );
        isNeedUpdateUI = isNeedUpdateUI || result.isNeedUpdateUI;
        noticeMessage = result.noticeMessage;
        break;
    }
  } catch (error) {
    console.error({ error });

    newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover;
    isNeedUpdateUI =
      (await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)])) || isNeedUpdateUI;
  }
  return { isNeedUpdateUI, noticeMessage };
};

export const restoreResetMessageToLocal = async (messages, prefixKey) => {
  try {
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    await getInteractor(prefixKey).LocalRestoreService.restoreFromResetMessage(messages);

    await restoreThread(prefixKey, accountId);

    // Get file record
    const attachmentIds = (await getInteractor(prefixKey).LocalFileGroupService.getAllFileIds()).map(
      item => item.file_id,
    );

    if (Array.isArray(attachmentIds) && attachmentIds.length > 0) {
      let reqAttachment = [];
      let offset = 0;
      let lastIndex = 100;
      do {
        reqAttachment = attachmentIds.slice(offset, lastIndex);
        await getFileList(prefixKey, reqAttachment);
        offset = lastIndex;
        lastIndex += 100;
      } while (reqAttachment.length === 100);
    }

    const minDisappearTime = await getInteractor(prefixKey).LocalMessageService.getMinDisappearTime();
    if (minDisappearTime > 0) {
      store.dispatch(
        ConversationActions.conversationSet({
          disappearTime: minDisappearTime,
        }),
      );
    }
  } catch (error) {
    console.error(error);
  }
};

// Unread message + thread message + notification in all active branch
export const setUnreadInAppLogo = async () => {
  let allBranchUnread = 0;
  let allBranchUnreadObj = {}; // [{ branchId: totalUnread }]
  const activeBranchList = await LocalDbManagement.find({
    state: SystemConstant.STATE.active,
  });
  if (Array.isArray(activeBranchList) && activeBranchList.length > 0) {
    for (let index = 0; index < activeBranchList.length; index++) {
      const branch = activeBranchList[index];
      const branchPrefixKey = getPrefixKey(branch.account_id, branch.branch_id);
      const branchTotalUnreadInGroup = await getInteractor(branchPrefixKey).LocalGroupService.getTotalUnread();
      const branchTotalUnreadInThread =
        await getInteractor(branchPrefixKey).LocalThreadService.countTotalUnreadMessage();
      const numberUnreadNotice = (
        await getInteractor(branchPrefixKey).LocalNotificationService.getUnreadNormalNoticeInBranch(branch.branch_id)
      ).length;

      const totalUnread = branchTotalUnreadInGroup + branchTotalUnreadInThread + numberUnreadNotice;
      allBranchUnreadObj[branch.branch_id] = totalUnread;
      allBranchUnread = allBranchUnread + totalUnread;
    }
  }

  // Set count unread in all branch to App Logo
  LocalAppNotificationService.setBadgeCount(allBranchUnread);
  store.dispatch(
    SystemActions.systemSet({
      allBranchUnreadObj: allBranchUnreadObj,
    }),
  );
};

const handleInvalidRecallMsg = async (prefixKey, message) => {
  const isValidMessage = Boolean(message && message.id);
  if (!isValidMessage) return;

  if (false === Boolean(message.parentId)) {
    message.state = SystemConstant.STATE.inactive;
    message.content = "";
  }

  await getInteractor(prefixKey).LocalMessageService.saveFromRemote([
    toSnake({ ...message, isInvalidRecallTime: true }),
  ]);
};

const processMessageContent = async (newMessage, group, prefixKey, encryptTypeProtocol, encryptStatusInfo = null) => {
  let isNeedUpdateUI = false;
  let noticeMessage;

  if (await isBlockedSender(newMessage, prefixKey)) {
    newMessage.status = SystemConstant.MESSAGE_STATUS.block;
    return { isNeedUpdateUI, noticeMessage };
  }
  if (encryptStatusInfo?.type === SystemConstant.ENCRYPT_STATUS_TYPE.errorDistribute) {
    const senderKeyMess = { ...newMessage };
    await sendMessageEncryptStatus(prefixKey, group, senderKeyMess, encryptTypeProtocol, encryptStatusInfo);
  } else if (encryptStatusInfo?.type === SystemConstant.ENCRYPT_STATUS_TYPE.error) {
    await sendMessageEncryptStatus(prefixKey, group, newMessage, encryptTypeProtocol, encryptStatusInfo);
    isNeedUpdateUI = await handleEncryptionError(newMessage, prefixKey);
  } else if (newMessage.content) {
    isNeedUpdateUI = await handleValidMessage(newMessage, group, prefixKey, encryptTypeProtocol);
    noticeMessage = await generateNoticeMessage(newMessage, group, prefixKey);
  }
  // Trigger to UI
  if (checkTriggerMessageUI(prefixKey, newMessage)) {
    store.dispatch(ConversationActions.receivedRemoteMessage(newMessage));
  }

  // Check if this message is new reply message of inactive delete only me message => dispatch update view mode
  const selectedGroupId = store.getState().conversationRedux.selectedGroupId;
  const currentIncomingMessages = store.getState().conversationRedux.incomingMessages;
  const currentMessageList = currentIncomingMessages[selectedGroupId] || {};

  if (group.accountGroupRecallTime && newMessage.created <= group.accountGroupRecallTime) {
    await handleInvalidRecallMsg(prefixKey, newMessage);
  }

  if (Boolean(newMessage.threadId) && newMessage.groupId === selectedGroupId) {
    const totalParentDeleteOnlyMe = await getInteractor(prefixKey).LocalMessageService.count({
      parent_id: newMessage.threadId,
      send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
    });

    if (totalParentDeleteOnlyMe > 0) {
      const parentMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
        source_id: newMessage.threadId,
      });
      if (parentMessage && parentMessage.id) {
        store.dispatch(
          ConversationActions.conversationSet({
            incomingMessages: {
              ...currentIncomingMessages,
              [selectedGroupId]: {
                ...currentMessageList,
                [parentMessage.id]: toCamel({ ...parentMessage, isDeleteOnlyMe: true, modified: Date.now() }),
              },
            },
          }),
        );
      }
    }
  }
  return { isNeedUpdateUI, noticeMessage };
};

const isBlockedSender = async (newMessage, prefixKey) => {
  const checkSenderIdBlock = await getInteractor(prefixKey).LocalContactService.getContact(
    newMessage.accountId,
    newMessage.senderId,
  );
  return (
    newMessage.accountId !== newMessage.senderId &&
    checkSenderIdBlock &&
    checkSenderIdBlock.status === SystemConstant.CONTACT_STATUS.block
  );
};

const handleEncryptionError = async (newMessage, prefixKey) => {
  newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover;
  await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)]);
  return true;
};

const handleValidMessage = async (newMessage, group, prefixKey, encryptTypeProtocol) => {
  if (newMessage.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.duplicate) return;
  let isNeedUpdateUI = false;
  if (newMessage.sendType === SystemConstant.SEND_TYPE.botMessage) {
    newMessage.messageEncryptStatus = SystemConstant.MESSAGE_ENCRYPT_STATUS.done;
  }

  //Trả về isNewRecover: có cần thông báo gửi thêm tin nhắn bị thiếu ở giữa không
  //Trả về recoveredMessage : những tin nhắn đã khôi phục nhằm phục vụ mục đích update UI
  const messageOptions = convertString2JSON(newMessage.options, {});

  isNeedUpdateUI =
    (await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(newMessage)])) || isNeedUpdateUI;

  if (SystemConstant.ARR_CALLING_TYPES.includes(newMessage.sendType)) {
    await handleCallingMessage(prefixKey, newMessage.id, group);
  }

  if (newMessage.sendType === SystemConstant.SEND_TYPE.deleteMessage) {
    await updateParentDeleteMessageContent(prefixKey, newMessage);
  }

  const recoverList = await cipherMessageManager.decryptNormalMessage.decryptRecoverMessages(
    newMessage,
    group,
    messageOptions,
    prefixKey,
  );

  const lastRecover = recoverList.lastMessage;

  let waitingIndex = await cipherMessageManager.decryptNormalMessage.getWaitingIndex(
    prefixKey,
    group,
    newMessage,
    encryptTypeProtocol,
  );
  let lastIndex = await cipherMessageManager.decryptNormalMessage.getLastIndex(
    prefixKey,
    group,
    newMessage,
    encryptTypeProtocol,
  );
  waitingIndex = await cipherMessageManager.decryptNormalMessage.validateWaitingIndex(
    prefixKey,
    newMessage,
    group,
    encryptTypeProtocol,
    waitingIndex,
    lastIndex,
  );

  if (waitingIndex === -1) return;
  const isBreakIndex = lastIndex > waitingIndex;
  if (isBreakIndex) {
    await sendMessageEncryptStatus(prefixKey, group, lastRecover || newMessage, encryptTypeProtocol, {
      type: SystemConstant.ENCRYPT_STATUS_TYPE.lostMessage,
      code: SystemConstant.ENCRYPT_STATUS_CODE.errorBreakIndex,
    });
  } else if (recoverList.isAllDone) {
    await sendMessageEncryptStatus(prefixKey, group, lastRecover || newMessage, encryptTypeProtocol, {
      type: SystemConstant.ENCRYPT_STATUS_TYPE.success,
      code: SystemConstant.ENCRYPT_STATUS_CODE.successCommon,
    });
  }

  return isNeedUpdateUI;
};

const generateNoticeMessage = async (newMessage, group, prefixKey) => {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const groupSetting = await getInteractor(prefixKey).LocalGroupSettingService.findOne({
    account_id: accountId,
    group_id: newMessage.groupId,
    setting_sub_type: SystemConstant.SETTING_SUB_TYPE.NOTIFICATION_GROUP,
  });

  const isMutedGroup = checkValidGroupSettingOff(prefixKey, groupSetting, newMessage.groupId);
  const isBlockedOnGlobalBranch = Boolean(
    newMessage.branchId === SystemConstant.GLOBAL_BRANCH_ID && (await isBlockedSender(newMessage, prefixKey)),
  );
  const isInvolvedThread = await updateThread(prefixKey, toSnake(newMessage));

  if (!isMutedGroup && !isBlockedOnGlobalBranch && newMessage.status !== SystemConstant.MESSAGE_STATUS.read) {
    return {
      prefixKey,
      message: newMessage,
      isInvolvedThread: isInvolvedThread,
    };
  }

  return null;
};

const resetIndexFlowOldVersion = async (message, group, device, messageOptions, encryptTypeProtocol, prefixKey) => {
  if ((device != null && !isNewIvsE2EDevice(device)) || messageOptions?.prev_send_number == null) {
    await cipherMessageManager.encryptionErrorNotifier.resetWaitingIndex(
      message,
      group,
      encryptTypeProtocol,
      prefixKey,
    );
  }
};
